/**
 * Freelance work permit — individual photographers only.
 * Expiry date is set by admin after reviewing the document.
 */

export const FREELANCE_PERMIT_FILE_PREFIX = 'freelance_permit';

export const FREELANCE_PERMIT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

export const FREELANCE_PERMIT_MAX_SIZE = 10 * 1024 * 1024;

export const isFreelancePermitFile = (fileName) =>
  typeof fileName === 'string' && fileName.startsWith(FREELANCE_PERMIT_FILE_PREFIX);

export const photographerIsIndividual = (entityType) =>
  (entityType || 'individual') === 'individual';

export const getFreelancePermitStatus = (profile) => {
  if (!profile?.freelance_permit_url) return 'not_uploaded';
  if (!profile.freelance_permit_reviewed_at) return 'submitted';
  if (profile.freelance_permit_verified === true) return 'approved';
  return 'rejected';
};

export const getFreelancePermitStatusLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'مقبول';
    case 'submitted':
      return 'قيد المراجعة';
    case 'rejected':
      return 'مرفوض';
    default:
      return 'لم يُرفع بعد';
  }
};

export const getDaysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry.getTime() - today.getTime()) / 86400000);
};

/**
 * - no_date: admin has not set expiry yet
 * - valid: more than 30 days remaining
 * - expiring_soon: 8–30 days remaining
 * - critical: 1–7 days remaining
 * - expired: past expiry date
 */
export const getFreelanceExpiryStatus = (expiresAt) => {
  const daysLeft = getDaysUntilExpiry(expiresAt);
  if (daysLeft === null) return 'no_date';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 7) return 'critical';
  if (daysLeft <= 30) return 'expiring_soon';
  return 'valid';
};

export const getFreelanceExpiryStatusLabel = (status) => {
  switch (status) {
    case 'valid':
      return 'ساري';
    case 'expiring_soon':
      return 'يقارب على الانتهاء';
    case 'critical':
      return 'ينتهي قريباً';
    case 'expired':
      return 'منتهي';
    default:
      return 'بدون تاريخ انتهاء';
  }
};

export const getFreelanceExpiryBadgeClass = (status) => {
  switch (status) {
    case 'valid':
      return 'bg-green-100 text-green-700';
    case 'expiring_soon':
      return 'bg-amber-100 text-amber-800';
    case 'critical':
      return 'bg-orange-100 text-orange-800';
    case 'expired':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

export const validateFreelancePermitFile = (file) => {
  if (!file) return 'لم يتم اختيار ملف';
  if (!FREELANCE_PERMIT_ALLOWED_TYPES.includes(file.type)) {
    return 'يُقبل JPEG أو PNG أو WebP أو PDF';
  }
  if (file.size > FREELANCE_PERMIT_MAX_SIZE) {
    return 'الحجم الأقصى 10 ميغابايت';
  }
  return null;
};

export const notifyAdminFreelancePermitUpload = async (supabase, {
  userId,
  photographerName,
  documentUrl,
  isReplacement = false,
}) => {
  try {
    await supabase.from('admin_notifications').insert({
      type: 'freelance_permit_request',
      title: isReplacement ? '🔄 تحديث وثيقة عمل حر' : '📋 طلب مراجعة وثيقة عمل حر',
      message: isReplacement
        ? `المصور "${photographerName}" استبدل وثيقة العمل الحر ويحتاج مراجعة جديدة.`
        : `المصور "${photographerName}" رفع وثيقة عمل حر ويحتاج مراجعة.`,
      related_user_id: userId,
      related_data: {
        photographer_name: photographerName,
        document_url: documentUrl,
        is_replacement: isReplacement,
        submitted_at: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.warn('Failed to create admin freelance permit notification:', err);
  }
};

export const uploadFreelancePermit = async (supabase, {
  userId,
  file,
  photographerName = 'مصور',
  previousUrl = null,
}) => {
  const validationError = validateFreelancePermitFile(file);
  if (validationError) throw new Error(validationError);
  if (!userId) throw new Error('معرّف المستخدم غير متوفر');

  const ext = file.name.split('.').pop();
  const filePath = `${userId}/${FREELANCE_PERMIT_FILE_PREFIX}_${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('verification-docs')
    .upload(filePath, file, { cacheControl: '3600', upsert: false });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('verification-docs')
    .getPublicUrl(filePath);

  const publicUrl = urlData?.publicUrl || null;
  const now = new Date().toISOString();
  const isReplacement = !!previousUrl;

  const { error: updateError } = await supabase
    .from('photographer_profiles')
    .update({
      freelance_permit_url: publicUrl,
      freelance_permit_verified: false,
      freelance_permit_submitted_at: now,
      freelance_permit_reviewed_at: null,
      freelance_permit_rejection_reason: null,
      freelance_permit_expires_at: null,
      updated_at: now,
    })
    .eq('user_id', userId);

  if (updateError) throw updateError;

  await notifyAdminFreelancePermitUpload(supabase, {
    userId,
    photographerName,
    documentUrl: publicUrl,
    isReplacement,
  });

  return { publicUrl, filePath };
};

export const backfillFreelancePermitFromStorage = async (supabase, userId, files) => {
  if (!userId || !files?.length) return null;

  const permitFile = files
    .filter((f) => isFreelancePermitFile(f.name))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  if (!permitFile) return null;

  const { data: urlData } = supabase.storage
    .from('verification-docs')
    .getPublicUrl(`${userId}/${permitFile.name}`);

  const publicUrl = urlData?.publicUrl || null;
  if (!publicUrl) return null;

  const submittedAt = permitFile.created_at || new Date().toISOString();

  await supabase
    .from('photographer_profiles')
    .update({
      freelance_permit_url: publicUrl,
      freelance_permit_submitted_at: submittedAt,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  return publicUrl;
};

export const matchesFreelanceFilter = (profile, filterKey) => {
  if (!photographerIsIndividual(profile?.entity_type)) return false;

  const permitStatus = getFreelancePermitStatus(profile);

  if (filterKey === 'freelance_pending') {
    return permitStatus === 'submitted';
  }

  if (permitStatus !== 'approved') return false;

  const expiryStatus = getFreelanceExpiryStatus(profile?.freelance_permit_expires_at);
  switch (filterKey) {
    case 'freelance_expiring_soon':
      return expiryStatus === 'expiring_soon';
    case 'freelance_critical':
      return expiryStatus === 'critical';
    case 'freelance_expired':
      return expiryStatus === 'expired';
    case 'freelance_valid':
      return expiryStatus === 'valid';
    case 'freelance_no_expiry':
      return expiryStatus === 'no_date';
    default:
      return false;
  }
};

export const sortByFreelanceExpiry = (list, ascending = true) => {
  return [...list].sort((a, b) => {
    const aDate = a.freelance_permit_expires_at ? new Date(a.freelance_permit_expires_at).getTime() : Infinity;
    const bDate = b.freelance_permit_expires_at ? new Date(b.freelance_permit_expires_at).getTime() : Infinity;
    return ascending ? aDate - bDate : bDate - aDate;
  });
};
